/**
 * Pacing Report Custom Hook
 *
 * Fetches current OTB data and pickup data to calculate prior period values.
 * Prior OTB is calculated as: currentOTB - pickup (for the comparison window)
 */

import { useQueries } from "@tanstack/react-query";
import moment from "moment";
import { api } from "../api";
import {
  ComparisonPeriod,
  RevenueSummaryMonth,
  AnnualBudgetMonth,
} from "../../pages/dashboard/components/PickupReport/PacingReport/helpers";
import {
  getComparisonShiftDays,
  transformToPacingData,
} from "../../pages/dashboard/components/PickupReport/PacingReport/helpers/pacingTransformers";
import { useGetOnboarding } from "./use-onboarding";

export function usePacingReport(
  userId: string,
  year: number,
  comparisonPeriod: ComparisonPeriod = "7_days",
  options: { enabled?: boolean } = {}
) {
  const { enabled = true } = options;
  const { data: onboardingData } = useGetOnboarding(userId);
  const fallbackBudget: AnnualBudgetMonth[] =
    onboardingData?.userMetadata?.annualBudget || [];

  const shiftDays = getComparisonShiftDays(comparisonPeriod);
  const startDate = `${year}-01-01`;
  const endDate = `${year}-12-31`;

  // PickUp window: bookings made in the last N days
  const today = moment().format("YYYY-MM-DD");
  const pickupStartDate = moment().subtract(shiftDays, "days").format("YYYY-MM-DD");

  const queries = useQueries({
    queries: [
      // Query 1: RevenueSummary for full year (current OTB + embedded budget + STLY)
      {
        queryKey: ["pacing-report", "summary", userId, year],
        queryFn: () =>
          api.getMetrics(userId, {
            type: "RevenueSummary",
            startDate,
            endDate,
            stly: true,
          }),
        enabled: !!year && enabled,
        staleTime: 10 * 60 * 1000,
      },
      // Query 2: PickUp for the comparison window (to calculate prior OTB)
      // startDate/endDate = booking window (when reservations were made)
      // targetStartDate/targetEndDate = stay period (when guests stay)
      {
        queryKey: ["pacing-report", "pickup", userId, year, comparisonPeriod],
        queryFn: () =>
          api.getMetrics(userId, {
            type: "PickUp",
            startDate: pickupStartDate,
            endDate: today,
            targetStartDate: startDate,
            targetEndDate: endDate,
            includeComparison: true,
          }),
        enabled: !!year && enabled,
      },
    ],
  });

  const [summaryQuery, pickupQuery] = queries;
  const isLoading = summaryQuery.isLoading || pickupQuery.isLoading;
  const isFetching = summaryQuery.isFetching || pickupQuery.isFetching;
  const error = summaryQuery.error || pickupQuery.error;

  const hasBothDataSources = summaryQuery.data && pickupQuery.data;
  const data =
    hasBothDataSources && !isLoading
      ? transformToPacingData(
          summaryQuery.data as RevenueSummaryMonth[],
          pickupQuery.data,
          fallbackBudget,
          year
        )
      : undefined;

  return {
    data,
    isLoading,
    isFetching,
    error,
    refetch: () => {
      summaryQuery.refetch();
      pickupQuery.refetch();
    },
  };
}
